/**
 * The document library (#6232): the business documents and calls the strategy
 * report is written from.
 *
 * Pure content surface: calls show their transcript, documents show their
 * text — actions extracted from calls live on /optimizations, not here.
 * Uploads accept the major document types (.docx, .pdf, .txt, .md) and
 * classify server-side (#6230): a call-typed upload joins the call pipeline
 * (extraction fires silently); everything else is read for the strategy
 * report.
 *
 * Laid out flat like the other Assets tabs: the controls (search, sort, view,
 * Upload) are handed up to the Assets toolbar through `onControlsReady`, as
 * Media's are (#6676), and the content sits directly below with no heading.
 * Two views: a tile grid (default) and a divided row list; both open the
 * same back-button detail.
 */

import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue } from 'jotai';
import { Download, Eye, FileText, LayoutGrid, List, Loader2, Pencil, Phone, Search, Trash2, Upload } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { SimpleTooltip } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

import { organizationDataAtom, documentsViewModeAtom } from '@/store';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/glitch-ui/spinner';
import { EmptyState } from '@/components/glitch-ui/empty-state';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { DataTable } from '@/components/glitch-ui/data-table';
import type { ColumnDef } from '@tanstack/react-table';
import { PlaybookRenderer } from '@/components/business/strategy-doc-playbook';
import { EditMetaModal } from '@/components/call-actions/edit-meta-modal';
import { callTranscriptQueryOptions, getCallTranscriptText } from '@/lib/callActions';
import {
  getStrategyDocMarkdown,
  listDocuments,
  setStrategyDocArchived,
  uploadStrategyDoc,
  type DocumentListItem,
} from '@/lib/strategy/api';

const ACCEPTED_DOC_TYPES = '.md,.markdown,.txt,.pdf,.docx';

function formatDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString();
}

const FILE_TYPE_LABELS: Record<string, string> = {
  md: 'MD',
  markdown: 'MD',
  txt: 'TXT',
  pdf: 'PDF',
  docx: 'DOCX',
};

/** The type chip label: the file extension when known, else the kind. */
function fileTypeLabel(item: DocumentListItem): string {
  // Meet calls and calls added by hand are calls. An uploaded transcript is a
  // file whose text read like a call, which is not the same as knowing it was
  // one, so it carries its file type like any other upload.
  if (item.kind === 'call' && item.source !== 'upload') return 'Call';
  const ext = item.filename?.split('.').pop()?.toLowerCase() ?? '';
  return FILE_TYPE_LABELS[ext] ?? (item.kind === 'call' ? 'File' : 'Document');
}


/** Attendees render as avatars only; the name lives in the hover tooltip.
 * Profile picture when the org member has one, else the initial fallback
 * used by the account menu. */
function AttendeeIcons({ attendees }: { attendees: { name: string; picture: string | null }[] }) {
  return (
    <span className='inline-flex items-center gap-1'>
      {attendees.map((attendee) => (
        <SimpleTooltip key={attendee.name} content={attendee.name}>
          <Avatar className='h-6 w-6' aria-label={attendee.name}>
            {attendee.picture && <AvatarImage src={attendee.picture} alt={attendee.name} />}
            <AvatarFallback className='rounded-full bg-[#290BD9] bg-opacity-10 text-[#290BD9] text-[10px]'>
              {attendee.name ? attendee.name.charAt(0).toUpperCase() : 'U'}
            </AvatarFallback>
          </Avatar>
        </SimpleTooltip>
      ))}
    </span>
  );
}

type SortKey = 'title' | 'type' | 'attendees' | 'added';
type SortState = { key: SortKey; dir: 'asc' | 'desc' };

// The grid's own order, chosen from its dropdown. The table sorts itself from
// its columns, the house way (#6715), so only the grid needs this.
const SORT_ACCESSORS: Record<SortKey, (item: DocumentListItem) => string | number> = {
  title: (i) => (i.title || '').toLowerCase(),
  type: (i) => fileTypeLabel(i),
  attendees: (i) => i.attendees.map((a) => a.name).join(', ').toLowerCase(),
  added: (i) => new Date(i.occurred_at ?? i.created_at).getTime(),
};

function sortItems(items: DocumentListItem[], sort: SortState): DocumentListItem[] {
  const accessor = SORT_ACCESSORS[sort.key];
  const factor = sort.dir === 'asc' ? 1 : -1;
  return [...items].sort((a, b) => {
    const va = accessor(a);
    const vb = accessor(b);
    if (va === vb) return 0;
    return (va < vb ? -1 : 1) * factor;
  });
}

/** Curated sort choices for the grid, mapped onto the shared sort state. */
const GRID_SORT_OPTIONS: { value: string; label: string; sort: SortState }[] = [
  { value: 'added-desc', label: 'Newest first', sort: { key: 'added', dir: 'desc' } },
  { value: 'added-asc', label: 'Oldest first', sort: { key: 'added', dir: 'asc' } },
  { value: 'title-asc', label: 'Title A to Z', sort: { key: 'title', dir: 'asc' } },
  { value: 'title-desc', label: 'Title Z to A', sort: { key: 'title', dir: 'desc' } },
  { value: 'type-asc', label: 'Type', sort: { key: 'type', dir: 'asc' } },
];

type Selected = { kind: 'call' | 'document'; id: number } | null;

export function DocumentsLibrary({
  businessId,
  onControlsReady,
}: {
  businessId: number;
  // The Assets page's toolbar slot: this tab's controls are drawn there.
  onControlsReady?: (controls: React.ReactNode) => void;
}) {
  const orgId = useAtomValue(organizationDataAtom)?.id ?? 0;
  const queryClient = useQueryClient();
  const [view, setView] = useAtom(documentsViewModeAtom);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Selected>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortState>({ key: 'added', dir: 'desc' });

  // Download the item's text content (the transcript, or the extracted
  // markdown for documents - the original binary is not retained).
  const downloadItem = async (item: DocumentListItem) => {
    const text =
      item.kind === 'call' ? (await getCallTranscriptText(item.id)).text : await getStrategyDocMarkdown(orgId, item.id);
    const fallbackName = `${(item.title || 'document').replace(/[^\w\- ]+/g, '').trim() || 'document'}${
      item.kind === 'call' ? '.txt' : '.md'
    }`;
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = item.filename ?? fallbackName;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const docsQuery = useQuery({
    queryKey: ['strategy', orgId, 'documents', businessId],
    queryFn: () => listDocuments(orgId, businessId),
    enabled: orgId > 0 && businessId > 0,
  });
  const items = docsQuery.data ?? [];
  // The house table sorts itself from the columns' accessors (#6715).
  const filtered = items.filter((i) => (i.title ?? '').toLowerCase().includes(search.toLowerCase()));

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadStrategyDoc(orgId, businessId, file),
    onSuccess: () => {
      // Uploads never trigger extraction: action generation from calls is
      // an ADMIN tool (ruling 2026-08-20). A call-classified upload is
      // simply visible as a call in the library.
      setUploadError(null);
      queryClient.invalidateQueries({ queryKey: ['strategy'] });
      queryClient.invalidateQueries({ queryKey: ['call-transcripts'] });
      // No auto-opened viewer: the fresh item appearing in the library IS the
      // confirmation; opening it stays the user's click.
    },
    onError: (err: Error) => setUploadError(err.message),
  });

  // The Add modal: device files only (browse or drag-and-drop). Calls
  // arrive via your account team; action generation is an admin tool.
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const submitFiles = (files: FileList | File[]) => {
    const list = Array.from(files).filter(Boolean);
    if (list.length === 0) return;
    list.forEach((file) => uploadMutation.mutate(file));
    setIsAddModalOpen(false);
  };

  // The modal renders from the list item.
  const selectedItem = selected ? items.find((i) => i.kind === selected.kind && i.id === selected.id) : undefined;

  const controlsElement = (
    <div className='flex flex-wrap items-center gap-2'>
    <div className='relative'>
      <Search className='absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none' />
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder='Search'
        className='pl-8 h-9 w-48 bg-white'
      />
    </div>
    {view === 'grid' && (
      <Select
        value={GRID_SORT_OPTIONS.find((o) => o.sort.key === sort.key && o.sort.dir === sort.dir)?.value ?? 'added-desc'}
        onValueChange={(value) => {
          const option = GRID_SORT_OPTIONS.find((o) => o.value === value);
          if (option) setSort(option.sort);
        }}
      >
        <SelectTrigger className='h-9 w-36 bg-white' aria-label='Sort documents'>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {GRID_SORT_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )}
    <div className='flex h-9 rounded-md border border-gray-300 overflow-hidden' role='group' aria-label='View mode'>
      <button
        type='button'
        aria-label='Grid view'
        title='Grid view'
        aria-pressed={view === 'grid'}
        className={`px-2.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#290BD9] ${
          view === 'grid' ? 'bg-gray-100 text-gray-900' : 'bg-white text-gray-500 hover:text-gray-700'
        }`}
        onClick={() => setView('grid')}
      >
        <LayoutGrid className='h-4 w-4' />
      </button>
      <button
        type='button'
        aria-label='Table view'
        title='Table view'
        aria-pressed={view === 'list'}
        className={`px-2.5 border-l border-gray-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#290BD9] ${
          view === 'list' ? 'bg-gray-100 text-gray-900' : 'bg-white text-gray-500 hover:text-gray-700'
        }`}
        onClick={() => setView('list')}
      >
        <List className='h-4 w-4' />
      </button>
    </div>
    <Button
      variant='outline'
      className='h-9 text-gray-900'
      onClick={() => setIsAddModalOpen(true)}
    >
      {uploadMutation.isPending ? (
        <Loader2 className='h-4 w-4 mr-2 animate-spin' />
      ) : (
        <Upload className='h-4 w-4 mr-2' />
      )}
      Upload
    </Button>
    </div>
  );

  useEffect(() => {
    if (onControlsReady) onControlsReady(controlsElement);
  }, [onControlsReady, search, view, sort, uploadMutation.isPending]);

  const documentColumns: ColumnDef<DocumentListItem>[] = [
    {
      id: 'title',
      header: 'Document',
      accessorFn: (item) => (item.title || '').toLowerCase(),
      meta: { className: 'px-6' },
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className='flex min-w-0 items-center'>
            <div className='h-8 w-8 flex-shrink-0 rounded-full bg-[#290BD9] bg-opacity-10 flex items-center justify-center'>
              {item.kind === 'call' && item.source === 'meet' ? (
                <img src='/google-meet.svg' alt='Google Meet' className='h-4 w-4' />
              ) : item.kind === 'call' && item.source !== 'upload' ? (
                <Phone className='h-4 w-4 text-[#290BD9]' />
              ) : (
                <FileText className='h-4 w-4 text-[#290BD9]' />
              )}
            </div>
            <div className='ml-4 min-w-0'>
              <div className='text-sm font-medium text-gray-900 truncate'>{item.title || 'Untitled'}</div>
              {item.filename && <div className='text-xs text-gray-500 truncate'>{item.filename}</div>}
            </div>
          </div>
        );
      },
    },
    {
      id: 'type',
      header: 'Type',
      accessorFn: (item) => fileTypeLabel(item),
      meta: { className: 'px-6 w-32' },
      cell: ({ row }) => (
        <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600'>
          {fileTypeLabel(row.original)}
        </span>
      ),
    },
    {
      id: 'attendees',
      header: 'Attendees',
      accessorFn: (item) => item.attendees.map((a) => a.name).join(', ').toLowerCase(),
      meta: { className: 'px-6' },
      cell: ({ row }) =>
        row.original.attendees.length > 0 ? (
          <AttendeeIcons attendees={row.original.attendees} />
        ) : (
          <span className='text-sm text-gray-500'>-</span>
        ),
    },
    {
      id: 'added',
      header: 'Added',
      accessorFn: (item) => new Date(item.occurred_at ?? item.created_at).getTime(),
      meta: { className: 'px-6 w-40' },
      cell: ({ row }) => (
        <>
          <div className='text-sm text-gray-500'>{formatDate(row.original.occurred_at ?? row.original.created_at)}</div>
          {row.original.added_by && <div className='text-xs text-gray-500'>by {row.original.added_by}</div>}
        </>
      ),
    },
    {
      id: 'actions',
      header: () => null,
      enableSorting: false,
      meta: { className: 'px-6 w-24 text-right' },
      cell: ({ row }) => (
        <div className='inline-flex items-center gap-1'>
          <Button
            variant='ghost'
            className='h-8 w-8 p-0 text-gray-500 hover:text-gray-900'
            aria-label='View'
            title='View'
            onClick={(e) => {
              e.stopPropagation();
              setSelected({ kind: row.original.kind, id: row.original.id });
            }}
          >
            <Eye className='h-4 w-4' />
          </Button>
          <Button
            variant='ghost'
            className='h-8 w-8 p-0 text-gray-500 hover:text-gray-900'
            aria-label='Download'
            title='Download'
            onClick={(e) => {
              e.stopPropagation();
              void downloadItem(row.original);
            }}
          >
            <Download className='h-4 w-4' />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      {uploadError && (
        <div className='mb-3 border-l-4 border-[#FCA5A5] bg-[#FEE2E2] text-[#B91C1C] p-3 text-sm'>{uploadError}</div>
      )}
      <Dialog open={isAddModalOpen} onOpenChange={(o) => !o && setIsAddModalOpen(false)}>
        <DialogContent className='sm:max-w-md' aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Add documents</DialogTitle>
          </DialogHeader>
          <label
            className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed px-6 py-10 text-center transition-colors ${
              isDragActive ? 'border-[#290BD9] bg-[#EDE9FE]' : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragActive(true);
            }}
            onDragLeave={() => setIsDragActive(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragActive(false);
              submitFiles(e.dataTransfer.files);
            }}
          >
            <Upload className='h-6 w-6 text-gray-400' />
            <p className='text-sm font-medium text-gray-900'>Drag and drop files here, or click to browse</p>
            <p className='text-xs text-gray-500'>Documents and transcripts: .docx, .pdf, .txt, .md, .vtt, .srt</p>
            <input
              type='file'
              multiple
              accept={ACCEPTED_DOC_TYPES}
              className='hidden'
              onChange={(e) => {
                if (e.target.files) submitFiles(e.target.files);
                e.target.value = '';
              }}
            />
          </label>
        </DialogContent>
      </Dialog>
      {docsQuery.isLoading ? (
        <div className='flex items-center justify-center gap-3 min-h-[50vh]'>
          <Spinner size='md' className='text-gray-500' />
          <span className='text-gray-500'>Loading documents...</span>
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<FileText className='h-12 w-12 text-gray-500' strokeWidth={1} />}
          title='No Documents Yet'
          description='Upload additional documents to provide better business context (.docx, .pdf, .txt, .md).'
        />
      ) : filtered.length === 0 ? (
        <div className='py-6 text-center text-sm text-gray-500'>No documents match your search.</div>
      ) : view === 'grid' ? (
        <div className='grid gap-4 grid-cols-3 xl:grid-cols-4'>
          {sortItems(filtered, sort).map((item) => (
            <DocumentTile
              key={`${item.kind}-${item.id}`}
              item={item}
              onOpen={() => setSelected({ kind: item.kind, id: item.id })}
              onDownload={() => void downloadItem(item)}
            />
          ))}
        </div>
      ) : (
        // The table meets the card's edges, as on Landing Pages and
        // Conversions (#6715): it pulls out through the tab panel's padding,
        // so its own cell padding is the only inset. The grid view still
        // wants that panel padding, so only this branch does it.
        // The house table (#6715): it brings the heading row, the sort
        // arrows, the cell padding and the flush edges, which this one used
        // to carry itself and kept drifting from. -mt-6 pulls it through the
        // tab panel's top padding, which the grid still wants.
        <div className='-mt-6'>
          <DataTable<DocumentListItem>
            data={filtered}
            columns={documentColumns}
            variant='flush'
            pageSize={999}
            initialSorting={[{ id: 'added', desc: true }]}
            getRowId={(item) => `${item.kind}-${item.id}`}
            onRowClick={(item) => setSelected({ kind: item.kind, id: item.id })}
            emptyState='No documents match your search.'
          />
        </div>
      )}
      {selectedItem && (
        <DocumentViewerModal
          orgId={orgId}
          businessId={businessId}
          item={selectedItem}
          onClose={() => setSelected(null)}
          onDownload={() => void downloadItem(selectedItem)}
        />
      )}
    </div>
  );
}

/** Grid card mirroring the assets#media layout: letterboxed "thumbnail"
 * zone on slate, info strip below. Documents have no image, so the zone
 * holds the type icon. */
function DocumentTile({
  item,
  onOpen,
  onDownload,
}: {
  item: DocumentListItem;
  onOpen: () => void;
  onDownload: () => void;
}) {
  return (
    <div
      className='flex flex-col rounded-lg border border-gray-200 bg-white overflow-hidden cursor-pointer hover:shadow-md transition-shadow'
      onClick={onOpen}
      role='button'
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <div className='relative h-36 bg-slate-100 flex items-center justify-center'>
        {item.kind === 'call' && item.source === 'meet' ? (
          <img src='/google-meet.svg' alt='Google Meet' className='h-10 w-10' />
        ) : item.kind === 'call' && item.source !== 'upload' ? (
          <Phone className='h-10 w-10 text-gray-400' strokeWidth={1.5} />
        ) : (
          <FileText className='h-10 w-10 text-gray-400' strokeWidth={1.5} />
        )}
      </div>
      <div className='p-2.5 flex flex-col flex-1 gap-1.5'>
        <p className='text-base font-medium text-gray-900 truncate' title={item.title || 'Untitled'}>
          {item.title || 'Untitled'}
        </p>
        <div className='flex items-center gap-1.5 flex-wrap'>
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600'>
            {fileTypeLabel(item)}
          </span>
          <span className='text-sm text-gray-500'>{formatDate(item.occurred_at ?? item.created_at)}</span>
        </div>
        {item.filename && (
          <p className='text-sm text-gray-500 truncate' title={item.filename}>
            {item.filename}
          </p>
        )}
        {item.attendees.length > 0 && <AttendeeIcons attendees={item.attendees} />}
        {item.added_by && <p className='text-sm text-gray-500 truncate'>Added by {item.added_by}</p>}
        <div className='mt-auto flex gap-2 pt-1.5'>
          <Button
            variant='outline'
            className='h-8 flex-1 text-gray-900'
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
          >
            <Eye className='h-4 w-4 mr-2' /> View
          </Button>
          <Button
            variant='outline'
            className='h-8 flex-1 text-gray-900'
            onClick={(e) => {
              e.stopPropagation();
              onDownload();
            }}
          >
            <Download className='h-4 w-4 mr-2' /> Download
          </Button>
        </div>
      </div>
    </div>
  );
}

/** The viewer (#6232): the selected item's CONTENT in a modal — transcripts
 * and documents alike. Actions extracted from calls are reviewed on
 * /optimizations, never here. */
function DocumentViewerModal({
  orgId,
  businessId,
  item,
  onClose,
  onDownload,
}: {
  orgId: number;
  businessId: number;
  item: DocumentListItem;
  onClose: () => void;
  onDownload: () => void;
}) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='sm:max-w-4xl' aria-describedby={undefined}>
        {item.kind === 'call' ? (
          <CallModalContent businessId={businessId} item={item} onDownload={onDownload} />
        ) : (
          <DocumentModalContent orgId={orgId} item={item} onRemoved={onClose} onDownload={onDownload} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function CallModalContent({
  businessId,
  item,
  onDownload,
}: {
  businessId: number;
  item: DocumentListItem;
  onDownload: () => void;
}) {
  const { data } = useQuery(callTranscriptQueryOptions(item.id));
  const textQuery = useQuery({
    queryKey: ['call-transcript-text', item.id],
    queryFn: () => getCallTranscriptText(item.id),
    staleTime: Infinity, // transcripts are immutable
  });
  const [editOpen, setEditOpen] = useState(false);

  return (
    <>
      <DialogHeader>
        <DialogTitle className='flex items-center gap-1.5 min-w-0 pr-6'>
          <span className='truncate'>{item.title || 'Untitled call'}</span>
          <Button
            variant='ghost'
            className='h-8 w-8 p-0 flex-shrink-0 text-gray-500 hover:text-gray-900'
            aria-label='Edit call details'
            disabled={!data}
            onClick={() => setEditOpen(true)}
          >
            <Pencil className='h-4 w-4' />
          </Button>
        </DialogTitle>
        <div className='flex items-center gap-1.5 text-sm text-gray-500'>
          <span>
            {item.filename ? `${item.filename} · ` : ''}
            {formatDate(item.occurred_at ?? item.created_at)}
          </span>
          {item.attendees.length > 0 && <AttendeeIcons attendees={item.attendees} />}
        </div>
      </DialogHeader>
      {textQuery.isLoading ? (
        <div className='flex items-center gap-2 p-6 text-sm text-gray-500'>
          <Spinner size='sm' /> Loading transcript...
        </div>
      ) : textQuery.isError ? (
        <div className='border-l-4 border-[#FCA5A5] bg-[#FEE2E2] p-3 text-sm text-[#B91C1C]'>
          The transcript could not be loaded.
        </div>
      ) : (
        <div className='max-h-[65vh] overflow-y-auto whitespace-pre-wrap rounded-lg bg-gray-50 p-4 text-sm leading-relaxed text-gray-800'>
          {textQuery.data?.text}
        </div>
      )}
      <div className='flex justify-end'>
        <Button variant='outline' className='h-9 text-gray-900' onClick={onDownload}>
          <Download className='h-4 w-4 mr-2' /> Download
        </Button>
      </div>
      {data && (
        <EditMetaModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          businessId={businessId}
          transcript={data.transcript}
        />
      )}
    </>
  );
}

function DocumentModalContent({
  orgId,
  item,
  onRemoved,
  onDownload,
}: {
  orgId: number;
  item: DocumentListItem;
  onRemoved: () => void;
  onDownload: () => void;
}) {
  const queryClient = useQueryClient();
  const [removeOpen, setRemoveOpen] = useState(false);
  const markdownQuery = useQuery({
    queryKey: ['strategy', orgId, 'doc-run-md', item.id],
    queryFn: () => getStrategyDocMarkdown(orgId, item.id),
    staleTime: 5 * 60 * 1000,
  });
  const archiveMutation = useMutation({
    mutationFn: () => setStrategyDocArchived(orgId, item.id, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['strategy'] });
      onRemoved();
    },
  });

  return (
    <>
      <DialogHeader>
        <DialogTitle className='truncate pr-6'>{item.title || 'Untitled'}</DialogTitle>
        <div className='text-sm text-gray-500'>
          {item.filename ? `${item.filename} · ` : ''}
          Business document
        </div>
      </DialogHeader>
      {markdownQuery.isLoading ? (
        <div className='flex items-center gap-2 p-6 text-sm text-gray-500'>
          <Spinner size='sm' /> Loading document...
        </div>
      ) : markdownQuery.isError ? (
        <div className='p-6 text-sm text-[#B91C1C]'>{(markdownQuery.error as Error).message}</div>
      ) : (
        <div className='max-h-[65vh] overflow-y-auto rounded-lg border'>
          <PlaybookRenderer content={markdownQuery.data ?? ''} />
        </div>
      )}
      <div className='flex items-center justify-between'>
        <Button
          variant='ghost'
          className='h-9 w-9 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50'
          aria-label='Remove document'
          onClick={() => setRemoveOpen(true)}
        >
          <Trash2 className='h-4 w-4' />
        </Button>
        <Button variant='outline' className='h-9 text-gray-900' onClick={onDownload}>
          <Download className='h-4 w-4 mr-2' /> Download
        </Button>
      </div>
      <ConformationModal
        isOpen={removeOpen}
        onClose={() => setRemoveOpen(false)}
        onConfirm={() => archiveMutation.mutate()}
        title='Remove this document?'
        description='The document will be removed from the library and stop informing the Strategic Plan. Its record is kept for auditing.'
        confirmText='Remove'
        cancelText='Cancel'
        isLoading={archiveMutation.isPending}
      />
    </>
  );
}
